import React from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    Avatar,
    IconButton,
    Badge,
    Tooltip
} from '@mui/material';
import { Bell, Search, Settings, Menu } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { useNavigate } from 'react-router-dom';

interface HeaderProps {
    onDrawerToggle: () => void;
}

const Header: React.FC<HeaderProps> = ({ onDrawerToggle }) => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const { data: avisos } = useQuery({
        queryKey: ['dashboard-avisos', user?.id],
        queryFn: async () => {
            if (!user) return [];
            const response = await apiClient.get('dashboard/avisos');
            return response.data;
        },
        enabled: !!user,
        refetchInterval: 60000, // atualiza a cada minuto
    });

    const pendingCount = avisos?.length || 0;

    return (
        <AppBar
            position="sticky"
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backgroundColor: 'rgba(10, 12, 16, 0.8)',
                backdropFilter: 'blur(10px)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                boxShadow: 'none',
                width: '100%'
            }}
        >
            <Toolbar sx={{ justifyContent: 'space-between', gap: 2 }}>
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={onDrawerToggle}
                    sx={{ mr: 2, display: { md: 'none' }, color: '#7D8590' }}
                >
                    <Menu size={24} />
                </IconButton>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { xs: 0, md: 'auto' } }}>
                    <Tooltip title="Buscar">
                        <IconButton size="small" sx={{ color: '#7D8590' }}>
                            <Search size={20} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Notificações">
                        <IconButton
                            size="small"
                            sx={{ color: pendingCount > 0 ? '#10b981' : '#7D8590' }}
                            onClick={() => pendingCount > 0 && navigate(avisos[0]?.link || '/comissoes/historico')}
                        >
                            <Badge badgeContent={pendingCount} color="error" variant={pendingCount > 0 ? 'standard' : 'dot'}>
                                <Bell size={20} />
                            </Badge>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Configurações">
                        <IconButton size="small" sx={{ color: '#7D8590' }}>
                            <Settings size={20} />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box sx={{ height: 32, width: '1px', backgroundColor: 'rgba(255, 255, 255, 0.05)', mx: 1 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                            {user?.nomeCompleto}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#7D8590' }}>
                            {user?.grupoAsaas || 'Usuário'}
                        </Typography>
                    </Box>
                    <Avatar
                        sx={{
                            width: 36,
                            height: 36,
                            background: 'linear-gradient(45deg, #7928CA 30%, #9D5CE6 90%)',
                            fontSize: '0.9rem',
                            fontWeight: 700
                        }}
                    >
                        {user?.nomeCompleto?.charAt(0) || 'U'}
                    </Avatar>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default Header;
